<template>
  <div class="animated fadeIn">
    <div class="container">
      <b-modal
        ref="modalUnsavedChanges"
        :title="$t('Webportal.Dialog.UnsavedChanges.Title')"
        :ok-title="$t('Webportal.Dialog.UnsavedChanges.Ok')"
        :cancel-title="$t('Webportal.Dialog.UnsavedChanges.Cancel')"
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close
      >
        {{ $t("Webportal.Dialog.UnsavedChanges.Text") }}
      </b-modal>

      <b-modal
        ref="modalProcessingNote"
        :title="$t('Webportal.Actions.SurveyRevision.ProcessingNote.Title')"
        :ok-title="$t('Webportal.Dialog.Ok')"
        ok-only
      >
        <span v-html="$t('Webportal.Actions.SurveyRevision.ProcessingNote', { url: $router.resolve({name: 'Notification settings'}).href })"></span>
      </b-modal>

      <form @submit.prevent novalidate="true">
        <b-card class="mb-3" v-bind:class="{ spinner: isBusy }">
          <template slot="header">
            <h5>
              <template v-if="isEditMode">
                {{ $t("Webportal.Actions.SurveyRevision") }}
                <span v-if="surveyRevision">
                  #{{ surveyRevision.SurveyRevisionVisibleId }}
                </span>
                <small
                  v-if="surveyRevision"
                  class="float-right col-form-label-sm text-muted"
                >
                  {{ getRevisionCreatedText(surveyRevision) }}
                </small>
              </template>
              <template v-else>
                {{ $t("Webportal.Actions.SurveyRevision.Create") }}
              </template>
            </h5>

            <survey-revision-header
              class="pt-2"
              :surveyRevision="surveyRevision"
              :surveyInstance="
                surveyInstanceData ? surveyInstanceData.SurveyInstance : null
              "
              :statusSelection="!isReadOnly && isEditMode"
              :statusHistoryVisible="isEditMode"
              :relatedSurveyRevisionsVisible="isEditMode"
              @statusChanged="statusChanged"
            />
          </template>

          <div v-if="!surveyRevision">
            <span v-if="!isBusy">{{ $t("Webportal.NoDataFoundPlain") }}</span>
          </div>
          <div v-if="isInitialized && surveyRevision">
            <b-form-group
              v-if="isEditMode && (!isExplanationReadOnly || surveyRevision.Explanation)"
              :label="$t('Webportal.Actions.SurveyRevision.Explanation')"
              label-size="sm"
            >
              <b-form-textarea
                v-model="surveyRevision.Explanation"
                :placeholder="$t('Webportal.Actions.SurveyRevision.Explanation.Placeholder')"
                :readonly="isExplanationReadOnly"
                max-rows="6"
                class="app-ctrl"
                v-validate
                data-vv-name="Explanation"
              />
            </b-form-group>

            <b-list-group flush>
              <b-list-group-item class="pl-0 pr-0 app-ctrl">
                <b-button
                  block
                  squared
                  v-b-toggle="`collapse-general`"
                  variant="primary"
                  class="text-left"
                >
                  <b-form-row>
                    <b-col>
                      {{ $t("Webportal.Actions.SurveyRevision.General") }}
                    </b-col>
                    <b-col cols="auto">
                      <font-awesome-icon
                        size="lg"
                        :icon="[
                          'far',
                          isGeneralVisible == true
                            ? 'caret-circle-up'
                            : 'caret-circle-down',
                        ]"
                      />
                    </b-col>
                  </b-form-row>
                </b-button>

                <b-collapse
                  class="mt-2 app-ctrl"
                  v-model="isGeneralVisible"
                  :id="`collapse-general`"
                >
                  <b-form-row>
                    <b-col lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Actions.SurveyRevision.Remarks') }}
                          <span v-if="surveyInstanceData"
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.ClientLocation.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevision.Remarks"
                          :placeholder="
                            !isEditMode
                              ? $t('Webportal.Actions.SurveyRevision.Remarks.Placeholder')
                              : null
                          "
                          :readonly="isEditMode"
                          max-rows="6"
                          class="app-ctrl"
                          v-validate
                          data-vv-name="Remarks"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col v-if="isEditMode && needsTranslation" lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Translated') }}
                          <span v-if="surveyInstanceData"
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.Survey.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevision.RemarksTranslated"
                          :placeholder="$t('Webportal.Translated.Placeholder')"
                          :readonly="isReadOnly"
                          max-rows="6"
                          class="app-ctrl"
                          v-validate
                          data-vv-name="RemarksTranslated"
                        />
                      </b-form-group>
                    </b-col>
                  </b-form-row>

                  <file-uploader
                    ref="generalFileUploader"
                    :label="$te('Webportal.Actions.SurveyRevision.Attachments') ? $t('Webportal.Actions.SurveyRevision.Attachments') : null"
                    :description="$te('Webportal.Actions.SurveyRevision.Attachments.Description') ? $t('Webportal.Actions.SurveyRevision.Attachments.Description') : null"
                    :value="surveyRevision.Attachments"
                    :upload-url="attachmentUploadUrl"
                    :readonly="isReadOnly"
                    @is-busy-changed="isGeneralFileUploaderBusy = $event"
                  />

                  <survey-revision-comments
                    v-if="isEditMode"
                    v-model="surveyRevision.Comments"
                    :survey-revision-id="surveyRevision.SurveyRevisionId"
                    :readonly="isReadOnly"
                    @is-busy-changed="isGeneralCommentsBusy = $event"
                  />
                </b-collapse>
              </b-list-group-item>

              <b-list-group-item
                class="pl-0 pr-0 app-ctrl"
                v-for="(surveyRevisionEntry, index) of surveyRevision.Entries"
                :key="index"
              >
                <b-button
                  block
                  squared
                  v-b-toggle="`collapse-question-${index}`"
                  variant="primary"
                  class="text-left"
                >
                  <b-form-row>
                    <b-col>
                      {{ $t("Webportal.Question") }}&nbsp;
                      <span class="small">
                        {{ getQuestionText(surveyRevisionEntry) }}
                      </span>
                    </b-col>
                    <b-col cols="auto align-self-center">
                      <font-awesome-icon
                        size="lg"
                        :icon="[
                          'far',
                          surveyRevisionEntry._visible == true
                            ? 'caret-circle-up'
                            : 'caret-circle-down',
                        ]"
                      />
                    </b-col>
                  </b-form-row>
                </b-button>
                <b-collapse
                  class="mt-2"
                  v-model="surveyRevisionEntry._visible"
                  :id="`collapse-question-${index}`"
                >
                  <b-form-group
                    :label="$t('Webportal.Question')"
                    label-size="sm"
                  >
                    <b-form-select
                      v-model="surveyRevisionEntry.SurveyQuestionId"
                      :options="questionOptions"
                      class="h-auto"
                      style="white-space: normal; word-wrap: break-word"
                      v-validate="'required'"
                      :data-vv-name="`Entries[${index}].SurveyQuestionId`"
                      :disabled="isEditMode"
                      @input="questionInput(surveyRevisionEntry, $event)"
                    />
                  </b-form-group>

                  <b-form-row>
                    <b-col lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Actions.SurveyRevision.Remarks') }}
                          <span v-if="surveyInstanceData" 
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.ClientLocation.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevisionEntry.Remarks"
                          :placeholder="$t('Webportal.Actions.SurveyRevision.Remarks.Placeholder')"
                          :readonly="isEditMode"
                          max-rows="6"
                          class="app-ctrl"
                          v-validate="'required'"
                          :data-vv-name="`Entries[${index}].QuestionRemarks`"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col v-if="isEditMode && needsTranslation" lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Translated') }}
                          <span v-if="surveyInstanceData" 
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.Survey.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea
                          v-model="surveyRevisionEntry.RemarksTranslated"
                          :placeholder="$t('Webportal.Translated.Placeholder')"
                          :readonly="isReadOnly"
                          max-rows="6"
                          class="app-ctrl"
                          v-validate
                          :data-vv-name="`Entries[${index}].RemarksTranslated`"
                        />
                      </b-form-group>
                    </b-col>
                  </b-form-row>

                  <file-uploader
                    ref="entryFileUploader"
                    :label="$te('Webportal.Actions.SurveyRevision.Attachments') ? $t('Webportal.Actions.SurveyRevision.Attachments') : null"
                    :description="$te('Webportal.Actions.SurveyRevision.Attachments.Description') ? $t('Webportal.Actions.SurveyRevision.Attachments.Description') : null"
                    :value="surveyRevisionEntry.Attachments"
                    :upload-url="attachmentUploadUrl"
                    :upload-url-params="{
                      'survey-revision-entry-id':
                        surveyRevisionEntry.SurveyRevisionEntryId,
                    }"
                    :readonly="isReadOnly"
                    @is-busy-changed="
                      isBusyChanged(
                        surveyRevisionEntry,
                        '_isFileUploaderBusy',
                        $event
                      )
                    "
                  />

                  <survey-revision-comments
                    v-if="isEditMode"
                    v-model="surveyRevisionEntry.Comments"
                    :survey-revision-id="surveyRevision.SurveyRevisionId"
                    :survey-revision-entry-id="
                      surveyRevisionEntry.SurveyRevisionEntryId
                    "
                    :readonly="isReadOnly"
                    @is-busy-changed="
                      isBusyChanged(
                        surveyRevisionEntry,
                        '_isCommentsBusy',
                        $event
                      )
                    "
                  />

                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="float-right"
                    v-if="!isEditMode && index > 0"
                    @click="removeQuestion(surveyRevisionEntry)"
                  >
                    <font-awesome-icon icon="trash-alt" />
                    {{ $t("Webportal.Delete") }}
                  </b-button>
                </b-collapse>
              </b-list-group-item>
            </b-list-group>

            <b-button
              class="mt-3"
              variant="secondary"
              size="sm"
              v-if="
                !isEditMode &&
                (maximumQuestionCount == null ||
                  maximumQuestionCount > surveyRevision.Entries.length)
              "
              :disabled="!isFormValid"
              @click="addQuestion()"
            >
              <font-awesome-icon icon="plus" />
              {{ $t("Webportal.Actions.SurveyRevision.AddQuestion") }}
            </b-button>
          </div>

          <template slot="footer">
            <div class="float-right">
              <button
                type="button"
                @click="cancel"
                class="btn btn-secondary"
                :disabled="isBusy || isChildBusy"
              >
                <font-awesome-icon
                  :icon="['far', isEditMode ? 'arrow-left' : 'ban']"
                />
                {{ isEditMode ? $t("Webportal.Back") : $t("Webportal.Cancel") }}
              </button>
              <button
                v-if="!isReadOnly"
                type="button"
                @click="submit"
                class="btn btn-primary"
                :disabled="
                  !isFormChanged || !isFormValid || isBusy || isChildBusy
                "
              >
                <font-awesome-icon :icon="['far', 'dot-circle']" />
                {{ isEditMode ? $t("Webportal.Save") : $t("Webportal.Actions.SendRequest") }}
              </button>
            </div>
          </template>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import EventBus from "@/services/EventBus.js";
import ActionService from "@/services/ActionService";
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveyRevisionHeader from "./SurveyRevisionHeader";
import SurveyRevisionComments from "./SurveyRevisionComments";
import FileUploader from "@/components/FileUploader";
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";

export default {
  name: "SurveyRevision",

  mixins: [ AppConfig, SurveyRevisionMixin ],

  components: {
    SurveyRevisionHeader,
    SurveyRevisionComments,
    FileUploader,
  },

  props: {
    surveyInstanceId: null,
  },

  data() {
    return {
      isGeneralVisible: (APP_TARGET !== "porschesar" && APP_TARGET !== "skoda") ? true : false,
      isGeneralFileUploaderBusy: false,
      isGeneralCommentsBusy: false,
      isBusy: false,

      surveyRevision: null,
      surveyInstanceData: null,

      isInitialized: false
    };
  },

  computed: {
    isFormChanged() {
      return Object.keys(this.validationFields).some(
        (key) =>
          this.validationFields[key].dirty || this.validationFields[key].changed
      );
    },

    isFormValid() {
      return Object.keys(this.validationFields).every(
        (key) =>
          this.validationFields[key].valid === null ||
          this.validationFields[key].valid
      );
    },

    isEditMode() {
      return this.$route.params.id !== undefined;
    },

    isReadOnly() {
      return !this.surveyRevision || this.surveyRevision.IsReadOnly;
    },

    isExplanationReadOnly() {
      return this.isReadOnly || !this.surveyRevision || this.surveyRevision.IsExplanationReadOnly;
    },

    needsTranslation() {
      return this.surveyInstanceData?.SurveyInstance.Survey.DefaultLocalization != 
        this.surveyInstanceData?.SurveyInstance.ClientLocation.DefaultLocalization;
    },

    questionOptions() {
      return _(this.surveyInstanceData?.Questions)
        .groupBy((q) => q.ClusterText)
        .map((g) => ({
          label: _.first(g).ClusterText,
          options: g.map((cq) => ({
            text: `${cq.QuestionNumber ?? ""} ${cq.QuestionText}`,
            value: cq.SurveyQuestionId,
          })),
        }))
        .value();
    },

    attachmentUploadUrl() {
      return this.surveyRevision?.SurveyRevisionId
        ? process.env.BASE_URL +
            `api/action/survey-revision/${this.surveyRevision.SurveyRevisionId}/attachment`
        : null;
    },

    isChildBusy() {
      return (
        this.isGeneralFileUploaderBusy ||
        this.isGeneralCommentsBusy ||
        this.surveyRevision?.Entries.some(
          (e) => e._isCommentsBusy || e._isFileUploaderBusy
        )
      );
    },

    maximumQuestionCount() {
      return (APP_TARGET === "porschesar" || APP_TARGET === "skoda") ? 1 : null;
    },

    surveyRevisionForSubmit() {
      if (!this.isEditMode)
        return this.surveyRevision;
      else
      {
        var changedEntryRemarksTranslated = Object.entries(this.validationFields)
          .filter(([key, value]) => key.endsWith('.RemarksTranslated') && value.dirty)
          .map(([key, value]) => key);

        return _.pick(this.surveyRevision, [
          'SurveyRevisionId', 'SurveyInstanceId', 'StatusId',
          ... this.validationFields['Explanation']?.dirty ? ['Explanation'] : [],
          ... this.validationFields['RemarksTranslated']?.dirty ? ['RemarksTranslated'] : [],
          ... changedEntryRemarksTranslated,
          ... changedEntryRemarksTranslated.map(key => key.replace('.RemarksTranslated', '.SurveyRevisionEntryId'))
          ]);
      }
    }
  },

  async created() {
    this.initializeTheme();
    
    if (!this.isEditMode && !this.surveyInstanceId)
      this.$router.replace({ name: "notfound" });

    if (this.isEditMode) {
      await this.getSurveyRevision(this.$route.params.id);
      if (this.surveyRevision)
        await this.getSurveyInstanceData(this.surveyRevision.SurveyInstanceId);
    } else {
      await this.getSurveyInstanceData(this.surveyInstanceId);

      if (this.surveyInstanceData?.SurveyInstance) {
        if (!this.surveyInstanceData.SurveyInstance.CanCreateSurveyRevision) {
          const h = this.$createElement;

          this.$bvModal
            .msgBoxOk(
              [
                h("div", {
                  domProps: {
                    innerHTML: this.$t(
                      "Webportal.Dialog.SurveyRevisionCreation.Text",
                      {
                        days: `<b>${this.surveyInstanceData.SurveyInstance.SurveyRevisionCreationLimitInDays}</b>`,
                      }
                    ),
                  },
                }),
              ],
              {
                title: this.$t("Webportal.Dialog.SurveyRevisionCreation.Title"),
                okTitle: this.$t("Webportal.Dialog.Ok"),
                hideHeaderClose: false,
              }
            )
            .then(() => {
              this.$router.go(-1);
            });
        } else
          this.surveyRevision = {
            SurveyInstanceId:
              this.surveyInstanceData.SurveyInstance.SurveyInstanceId,
            ExternalSurveyInstanceId:
              this.surveyInstanceData.SurveyInstance.ExternalSurveyInstanceId,
            StatusId: this.SurveyRevisionStatus.New,
            Entries: [{ _visible: true }],
          };
      }
    }

    EventBus.$on("LocalizationChanged", this.localizationChanged);

    this.isInitialized = true;
  },

  beforeDestroy() {
    EventBus.$off("LocalizationChanged", this.localizationChanged);
  },

  beforeRouteLeave(to, from, next) {
    this.unsavedChanges((result) => {
      if (result !== false) next();
    });
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyInstanceData(this.surveyRevision.SurveyInstanceId);
    },

    getRegion(localization) {
      if (!localization || !localization.includes("-"))
        return "";
      else
        return localization.split("-")[1].toLowerCase();
    },

    isBusyChanged(surveyRevisionEntry, property, value) {
      this.$set(surveyRevisionEntry, property, value);
    },

    addQuestion() {
      this.surveyRevision.Entries.forEach((e) => (e._visible = false));
      this.surveyRevision.Entries.push({ _visible: true });
    },

    removeQuestion(surveyRevisionEntry) {
      this.surveyRevision.Entries.splice(
        this.surveyRevision.Entries.findIndex((e) => e == surveyRevisionEntry),
        1
      );
    },

    getQuestionText(surveyRevisionEntry) {
      let surveyQuestion = this.findQuestion(
        surveyRevisionEntry.SurveyQuestionId
      );

      return surveyQuestion || surveyRevisionEntry.QuestionText
        ? `${
            surveyQuestion?.QuestionNumber ??
            surveyRevisionEntry.QuestionNumber ??
            ""
          } ${surveyQuestion?.QuestionText ?? surveyRevisionEntry.QuestionText}`
        : null;
    },

    findQuestion(surveyQuestionId) {
      return this.surveyInstanceData?.Questions.find(
        (q) => q.SurveyQuestionId == surveyQuestionId
      );
    },

    questionInput(surveyRevisionEntry, value) {
      let question = this.findQuestion(value);
      surveyRevisionEntry.QuestionNumber = question?.QuestionNumber;
      surveyRevisionEntry.QuestionText = question?.QuestionText;
    },

    unsavedChanges(callback) {
      if (this.isFormChanged || this.isChildBusy) {
        let dialog = this.$refs.modalUnsavedChanges;

        dialog.$off();
        dialog.$on("ok", (_) => callback());
        dialog.$on("cancel", (_) => callback(false));
        dialog.show();
      } else callback();
    },

    statusChanged(statusId) {
      let currentStatus = this.surveyRevision.StatusId;
      this.surveyRevision.StatusId = statusId;

      // Submit and undo local status change on error
      this.submit().catch(() => {
        this.surveyRevision.StatusId = currentStatus;
      });
    },

    getSurveyInstanceData(surveyInstanceId) {
      this.isBusy = true;

      return ActionService.getSurveyInstanceData(surveyInstanceId)
        .then((response) => {
          this.surveyInstanceData = !_.isEmpty(response.data)
            ? response.data
            : null;
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    getSurveyRevision(surveyRevisionId) {
      this.isBusy = true;

      return ActionService.getSurveyRevision(surveyRevisionId)
        .then((response) => {
          let surveyRevision = !_.isEmpty(response.data) ? response.data : null;

          if (!_.isEmpty(surveyRevision?.Entries))
            surveyRevision.Entries[0]._visible = true;

          this.surveyRevision = surveyRevision;
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    cancel() {
      this.$router.go(-1);
    },

    async submit() {
      this.isBusy = true;

      try {
        let response = await ActionService.setSurveyRevision(
          this.surveyRevisionForSubmit
        );

        this.$validator.reset();

        this.$bvToast.toast(
          this.$t("Webportal.SaveSuccess.Text", {
            entity: this.$t("Webportal.Actions.SurveyRevision"),
          }),
          {
            autoHideDelay: 2000,
            variant: "success",
            noCloseButton: true,
          }
        );

        if (!this.isEditMode) {
          this.$router.replace({
            name: this.$router.currentRoute.name,
            params: { id: response.data.surveyRevisionId },
          });

          if (this.surveyInstanceData.SurveyInstance.ShowSurveyRevisionProcessingNote)
            this.$refs.modalProcessingNote.show();

          // Reload revision, also because component is not reloaded on eventual navigation change
          await this.getSurveyRevision(response.data.surveyRevisionId);

          // Upload all file attachments
          await this.$refs.generalFileUploader.uploadFiles();
          for (const fileUploader of this.$refs.entryFileUploader)
            await fileUploader.uploadFiles();
        } else {
          await this.getSurveyRevision(response.data.surveyRevisionId);
        }
      } catch (error) {
        console.error(error);

        this.$bvToast.toast(
          this.$t("Webportal.SaveFailed.Text", {
            entity: this.$t("Webportal.Actions.SurveyRevision"),
          }),
          {
            autoHideDelay: 10 * 1000,
            variant: "danger",
            noCloseButton: true,
          }
        );
        throw error;
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>